import type { Offer, OfferType } from '@makemydeal/dr-platform-types';
import { FINANCE } from '@makemydeal/dr-platform-types';
import type { PartialPenciledInputsWithAllTypes, PenciledInputsState } from '../../../types/commonTypes';
import * as types from '../../../types/actionTypes';
import { getFeatureToggle } from '../../../selectors/offerNodeSelectors';
import { penciledInputsOverrides } from '../helpers';

export const handleCreditDecisionCases = (
    state: PenciledInputsState,
    action: any,
    offerType: OfferType,
    offer: Offer,
    _isUserProgramQuotes: boolean
): PartialPenciledInputsWithAllTypes => {
    switch (action.type) {
        case types.CREDIT_DECISION_APPROVED: {
            const newFinancePenciledInputs = {
                ...state.byOfferType?.[FINANCE],
                creditDecisionEnabled: true,
                creditDecisionStatus: 'Approved',
                creditDecisionLender: {
                    name: action.payload.lenderName,
                    id: action.payload.lenderId
                }
            };
            if (!state.byOfferType) return state;
            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [FINANCE]: {
                        ...newFinancePenciledInputs
                    }
                }
            };
        }

        case types.APPLY_CREDIT_DECISION_DETAILS: {
            const { months, sellRate } = action.meta.paymentRequestOverride[FINANCE];
            const prevOverridesByTerm = state.byOfferType?.[FINANCE]?.overridesByTerm || {};
            const newOverridesByTerm = {
                ...prevOverridesByTerm,
                [months]: {
                    ...prevOverridesByTerm[months],
                    sellRate
                }
            };
            if (!state.byOfferType) return state;
            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [FINANCE]: {
                        ...state.byOfferType?.[FINANCE],
                        overridesByTerm: newOverridesByTerm
                    }
                }
            };
        }

        case types.UPDATED_CREDIT_DECISION_ENABLED: {
            const newState = {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [offerType]: {
                        ...state.byOfferType?.[offerType],
                        creditDecisionEnabled: action.payload
                    }
                }
            };

            if (action.payload) {
                newState.byOfferType[offerType] = {
                    ...newState.byOfferType[offerType],
                    // Same reason as SET_PROGRAM_FALLBACK_OVERRIDES below: left to default to false, the
                    // helper pins baseResidualTypeOverride to dollar and re-seeds from the lender's term
                    // data, discarding a percent-based residual override.
                    ...penciledInputsOverrides(
                        state,
                        offer,
                        undefined,
                        offerType,
                        getFeatureToggle({ offer }, 'enableLeaseResidualTreatment_F286227')
                    )
                };
            }

            return newState as PartialPenciledInputsWithAllTypes;
        }

        case types.SET_PROGRAM_FALLBACK_OVERRIDES: {
            if (!state.byOfferType) return state;
            // `leaseEndpointMiddleware` only dispatches this while enableLeaseResidualTreatment_F286227 is
            // on, so pass the toggle through rather than letting it default to false. Left false,
            // `penciledInputsOverrides` pins baseResidualTypeOverride to dollar and re-seeds the dollar
            // amounts from term data, so the payment payload prices a percent-based lease off the lender's
            // untouched dollar residual — a residual typed on the deal screen or in the payment grid
            // displays but never reaches Payment Services.
            return {
                ...state,
                byOfferType: {
                    ...state.byOfferType,
                    [offerType]: {
                        ...state.byOfferType?.[offerType],
                        ...penciledInputsOverrides(
                            state,
                            offer,
                            undefined,
                            offerType,
                            getFeatureToggle({ offer }, 'enableLeaseResidualTreatment_F286227')
                        )
                    }
                }
            };
        }

        default: {
            return state;
        }
    }
};
